/*
  [custom code by chris]

  Mobile sticky add-to-cart behavior for Kroop's product pages.

  This script does not create a second product form.
  The sticky button triggers the real Add to Cart button in the existing product form.
*/

(function () {
  'use strict';

  var selectors = {
    stickyBar: '[data-cl-sticky-atc]',
    stickyButton: '[data-cl-sticky-atc-button]',
    stickyButtonText: '[data-cl-sticky-atc-button-text]',
    stickyVariant: '[data-cl-sticky-atc-variant]',
    stickyPrice: '[data-cl-sticky-atc-price]',
    productForm: 'form[action*="/cart/add"]',
    mainAddButton: '[data-add-to-cart], button[name="add"], input[name="add"]',
    footer: '#shopify-section-footer, .footer, footer'
  };

  var classes = {
    visible: 'is-visible',
    bodyVisible: 'cl-sticky-atc-visible',
    variantHidden: 'cl-sticky-atc__variant--hidden'
  };

  var strings = {
    addToCart: 'Add to cart',
    soldOut: 'Sold out',
    unavailable: 'Unavailable'
  };

  var state = {
    stickyBar: null,
    stickyButton: null,
    stickyButtonText: null,
    stickyVariant: null,
    stickyPrice: null,
    productForm: null,
    mainAddButton: null,
    footer: null,
    buyButtonObserver: null,
    footerObserver: null,
    mainButtonIsVisible: true,
    footerIsVisible: false,
    isMobile: false,
    currentVariant: null
  };

  function init() {
    state.stickyBar = document.querySelector(selectors.stickyBar);

    if (!state.stickyBar) {
      return;
    }

    state.stickyButton = state.stickyBar.querySelector(selectors.stickyButton);
    state.stickyButtonText = state.stickyBar.querySelector(selectors.stickyButtonText);
    state.stickyVariant = state.stickyBar.querySelector(selectors.stickyVariant);
    state.stickyPrice = state.stickyBar.querySelector(selectors.stickyPrice);
    state.productForm = findProductForm();
    state.mainAddButton = findMainAddButton();
    state.footer = document.querySelector(selectors.footer);
    state.isMobile = window.matchMedia('(max-width: 768px)').matches;

    if (!state.stickyButton || !state.productForm || !state.mainAddButton) {
      return;
    }

    setInitialStrings();
    setInitialState();
    bindEvents();
    setupObservers();
    updateVisibility();
  }

  function setInitialStrings() {
    if (window.theme && window.theme.strings) {
      strings.addToCart = window.theme.strings.addToCart || strings.addToCart;
      strings.soldOut = window.theme.strings.soldOut || strings.soldOut;
      strings.unavailable = window.theme.strings.unavailable || strings.unavailable;
    }
  }

  function setInitialState() {
    var available = state.stickyBar.dataset.currentVariantAvailable === 'true';

    updateButtonState(available);
    updateButtonText(available ? strings.addToCart : strings.soldOut);
  }

  function bindEvents() {
    state.stickyButton.addEventListener('click', handleStickyButtonClick);

    document.addEventListener('variant:change', handleVariantChange);

    document.addEventListener('ajaxProduct:added', handleAjaxComplete);
    document.addEventListener('ajaxProduct:error', handleAjaxComplete);

    window.addEventListener(
      'resize',
      debounce(function () {
        state.isMobile = window.matchMedia('(max-width: 768px)').matches;
        updateVisibility();
      }, 150)
    );
  }

  function setupObservers() {
    if ('IntersectionObserver' in window) {
      setupBuyButtonObserver();
      setupFooterObserver();
      return;
    }

    window.addEventListener('scroll', throttle(handleScrollFallback, 100));
    handleScrollFallback();
  }

  function setupBuyButtonObserver() {
    if (state.buyButtonObserver) {
      state.buyButtonObserver.disconnect();
    }

    state.buyButtonObserver = new IntersectionObserver(
      function (entries) {
        var entry = entries[0];

        if (!entry) {
          return;
        }

        state.mainButtonIsVisible = entry.isIntersecting;
        updateVisibility();
      },
      {
        root: null,
        threshold: 0
      }
    );

    state.buyButtonObserver.observe(state.mainAddButton);
  }

  function setupFooterObserver() {
    if (!state.footer) {
      return;
    }

    if (state.footerObserver) {
      state.footerObserver.disconnect();
    }

    state.footerObserver = new IntersectionObserver(
      function (entries) {
        var entry = entries[0];

        if (!entry) {
          return;
        }

        state.footerIsVisible = entry.isIntersecting;
        updateVisibility();
      },
      {
        root: null,
        rootMargin: '160px 0px 0px 0px',
        threshold: 0
      }
    );

    state.footerObserver.observe(state.footer);
  }

  function handleScrollFallback() {
    if (!state.mainAddButton) {
      return;
    }

    var buttonRect = state.mainAddButton.getBoundingClientRect();
    var footerRect = state.footer ? state.footer.getBoundingClientRect() : null;

    state.mainButtonIsVisible = buttonRect.top < window.innerHeight && buttonRect.bottom > 0;
    state.footerIsVisible = footerRect ? footerRect.top < window.innerHeight : false;

    updateVisibility();
  }

  function updateVisibility() {
    if (!state.stickyBar) {
      return;
    }

    var shouldShow =
      state.isMobile &&
      !state.mainButtonIsVisible &&
      !state.footerIsVisible &&
      state.mainAddButton &&
      !isElementHidden(state.mainAddButton);

    state.stickyBar.classList.toggle(classes.visible, shouldShow);
    state.stickyBar.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
    document.body.classList.toggle(classes.bodyVisible, shouldShow);
  }

  function handleStickyButtonClick() {
    state.productForm = findProductForm();
    state.mainAddButton = findMainAddButton();

    if (!state.mainAddButton || state.mainAddButton.disabled) {
      return;
    }

    state.stickyButton.disabled = true;
    state.stickyButton.setAttribute('aria-busy', 'true');

    state.mainAddButton.click();

    window.setTimeout(function () {
      syncButtonFromMainButton();
      state.stickyButton.removeAttribute('aria-busy');
    }, 750);
  }

  function handleAjaxComplete() {
    syncButtonFromMainButton();

    if (state.stickyButton) {
      state.stickyButton.removeAttribute('aria-busy');
    }
  }

  function handleVariantChange(event) {
    if (!event || !event.detail) {
      return;
    }

    var variant = event.detail.variant;

    state.currentVariant = variant || null;

    if (!variant) {
      updateVariantText('');
      updatePriceText('');
      updateButtonState(false);
      updateButtonText(strings.unavailable);
      return;
    }

    updateVariantText(getVariantTitle(variant));
    updatePriceText(formatMoney(variant.price));
    updateButtonState(variant.available);
    updateButtonText(variant.available ? strings.addToCart : strings.soldOut);

    state.stickyBar.dataset.currentVariantId = variant.id;
    state.stickyBar.dataset.currentVariantAvailable = variant.available ? 'true' : 'false';
  }

  function updateVariantText(text) {
    if (!state.stickyVariant) {
      return;
    }

    if (!text || text === 'Default Title') {
      state.stickyVariant.textContent = '';
      state.stickyVariant.classList.add(classes.variantHidden);
      return;
    }

    state.stickyVariant.textContent = text;
    state.stickyVariant.classList.remove(classes.variantHidden);
  }

  function updatePriceText(text) {
    if (!state.stickyPrice || !text) {
      return;
    }

    state.stickyPrice.innerHTML = text;
  }

  function updateButtonState(isAvailable) {
    if (!state.stickyButton) {
      return;
    }

    state.stickyButton.disabled = !isAvailable;
  }

  function updateButtonText(text) {
    if (!state.stickyButtonText) {
      return;
    }

    state.stickyButtonText.textContent = text;
  }

  function syncButtonFromMainButton() {
    state.mainAddButton = findMainAddButton();

    if (!state.mainAddButton || !state.stickyButton) {
      return;
    }

    state.stickyButton.disabled = state.mainAddButton.disabled;
  }

  function findProductForm() {
    var stickySectionId = state.stickyBar ? state.stickyBar.dataset.sectionId : null;

    if (stickySectionId) {
      var section = document.querySelector('[data-section-id="' + stickySectionId + '"]');

      if (section) {
        var sectionForm = section.querySelector(selectors.productForm);

        if (sectionForm) {
          return sectionForm;
        }
      }
    }

    return document.querySelector(selectors.productForm);
  }

  function findMainAddButton() {
    var form = findProductForm();

    if (!form) {
      return null;
    }

    return form.querySelector(selectors.mainAddButton);
  }

  function getVariantTitle(variant) {
    if (!variant) {
      return '';
    }

    if (variant.public_title) {
      return variant.public_title;
    }

    if (variant.title && variant.title !== 'Default Title') {
      return variant.title;
    }

    var options = [];

    if (variant.option1) {
      options.push(variant.option1);
    }

    if (variant.option2) {
      options.push(variant.option2);
    }

    if (variant.option3) {
      options.push(variant.option3);
    }

    return options.join(' / ');
  }

  function formatMoney(cents) {
    if (window.theme && window.theme.Currency && typeof window.theme.Currency.formatMoney === 'function') {
      return window.theme.Currency.formatMoney(cents, window.theme.settings.moneyFormat);
    }

    return '$' + (cents / 100).toFixed(2);
  }

  function isElementHidden(element) {
    if (!element) {
      return true;
    }

    return element.offsetParent === null;
  }

  function debounce(callback, wait) {
    var timeout;

    return function () {
      var args = arguments;
      var context = this;

      window.clearTimeout(timeout);

      timeout = window.setTimeout(function () {
        callback.apply(context, args);
      }, wait);
    };
  }

  function throttle(callback, limit) {
    var waiting = false;

    return function () {
      if (waiting) {
        return;
      }

      callback.apply(this, arguments);
      waiting = true;

      window.setTimeout(function () {
        waiting = false;
      }, limit);
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('page:loaded', init);
})();